import { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ChevronDown, Clock } from "lucide-react";

const HoursFilter = ({ onHoursChange, initialHours = "last6hours", baseDate = new Date() }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(initialHours);
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [customHours, setCustomHours] = useState(1);
  const dropdownRef = useRef(null);

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  };

  const calculateHourRange = (option, customValue = null) => {
    const now = baseDate ? new Date(baseDate) : new Date();
    const endDate = new Date(now);
    let startDate = new Date(now);

    if (option === "custom" && customValue) {
      startDate.setHours(now.getHours() - customValue);
      if (startDate < new Date(now.setHours(0, 0, 0, 0))) {
        startDate = new Date(now.setHours(0, 0, 0, 0));
      }
      return { startDate, endDate };
    }

    switch (option) {
      case "last3hours":
        startDate.setHours(now.getHours() - 3);
        break;
      case "last6hours":
        startDate.setHours(now.getHours() - 6);
        break;
      case "last9hours":
        startDate.setHours(now.getHours() - 9);
        break;
      case "last12hours":
        startDate.setHours(now.getHours() - 12);
        break;
      case "allday":
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        break;
      default:
        startDate.setHours(now.getHours() - 6);
    }

    if (startDate < new Date(now.setHours(0, 0, 0, 0))) {
      startDate = new Date(now.setHours(0, 0, 0, 0));
    }

    return { startDate, endDate };
  };

  const formatDateTime = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
    setShowCustomPicker(false);

    if (option === "custom") {
      setShowCustomPicker(true);
    } else {
      const { startDate, endDate } = calculateHourRange(option);
      onHoursChange(formatDateTime(startDate), formatDateTime(endDate), option);
      setIsOpen(false);
    }
  };

  const handleCustomApply = () => {
    if (customHours >= 1 && customHours <= 24) {
      const { startDate, endDate } = calculateHourRange("custom", customHours);
      onHoursChange(formatDateTime(startDate), formatDateTime(endDate), `last${customHours}hours`);
      setIsOpen(false);
      setShowCustomPicker(false);
      setSelectedOption("custom");
    }
  };

  useEffect(() => {
    let optionToUse = initialHours;
    if (!isToday(baseDate) && initialHours !== "allday") {
      optionToUse = "allday";
      setSelectedOption("allday");
    }
    const { startDate, endDate } = calculateHourRange(optionToUse);
    onHoursChange(formatDateTime(startDate), formatDateTime(endDate), optionToUse);
  }, [baseDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowCustomPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getButtonLabel = () => {
    if (selectedOption === "custom") return `Last ${customHours} Hours`;
    switch (selectedOption) {
      case "last3hours": return "Last 3 Hours";
      case "last6hours": return "Last 6 Hours";
      case "last9hours": return "Last 9 Hours";
      case "last12hours": return "Last 12 Hours";
      case "allday": return "All Day";
      default: return "Last 6 Hours";
    }
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 bg-primary-cyan hover:bg-primary-cyan/85 text-white px-5 py-2 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
      >
        <Clock size={18} />
        <span>{getButtonLabel()}</span>
        <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 z-20 min-w-[220px] overflow-hidden">
          <div className="py-2">
            <button
              onClick={() => handleOptionClick("last3hours")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "last3hours" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 3 Hours
            </button>
            <button
              onClick={() => handleOptionClick("last6hours")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "last6hours" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 6 Hours
            </button>
            <button
              onClick={() => handleOptionClick("last9hours")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "last9hours" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 9 Hours
            </button>
            <button
              onClick={() => handleOptionClick("last12hours")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "last12hours" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Last 12 Hours
            </button>
            <button
              onClick={() => handleOptionClick("allday")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "allday" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              All Day
            </button>
            <div className="border-t border-gray-100 my-1"></div>
            <button
              onClick={() => handleOptionClick("custom")}
              className={`w-full text-left px-5 py-2.5 text-sm transition-colors whitespace-nowrap ${selectedOption === "custom" ? "bg-primary-cyan/10 text-primary-cyan font-medium" : "text-gray-700 hover:bg-gray-50"}`}
            >
              Custom Hours
            </button>
          </div>

          {showCustomPicker && (
            <div className="border-t border-gray-100 p-4 bg-gray-50">
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Number of Hours (1-24)</label>
                <input
                  type="number"
                  min="1"
                  max="24"
                  value={customHours}
                  onChange={(e) => setCustomHours(Math.min(24, Math.max(1, parseInt(e.target.value) || 1)))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-cyan/50 focus:border-primary-cyan bg-white"
                />
              </div>
              <button
                onClick={handleCustomApply}
                className="w-full bg-primary-cyan hover:bg-primary-cyan/85 text-white px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200 shadow-sm hover:shadow"
              >
                Apply Hours
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HoursFilter;